<template>
  <v-container fluid>
    <page-title-area>
      <h1>{{ $t('title') }}</h1>
    </page-title-area>

    <div v-if="!loading && places.length > 0" class="d-flex justify-end mb-4">
      <round-button
        icon="add_circle"
        @click="openEditor(null)"
        data-testid="create_button"
      >
        {{ $t('button.create') }}
      </round-button>
    </div>

    <r-type-place-empty-state
      v-if="!loading && places.length === 0"
      @create="openEditor(null)"
    />

    <r-type-place-table
      v-else
      :places="places"
      :sensors="sensors"
      :loading="loading"
      @edit="openEditor"
      @delete="confirmDelete"
    />

    <r-type-place-editor
      v-model="editorVisible"
      :place="editTarget"
      @saved="onSaved"
    />

    <confirm-dialog
      v-model="deleteConfirmDialogVisible"
      :message="$t('confirm_delete', { placeName: deleteTarget.name })"
      @yes="deletePlace(deleteTarget)"
    />
  </v-container>
</template>

<i18n lang="yaml">
ja:
  title: ネズミ
  button:
    create: 管理施設を作成
  confirm_delete: '"{placeName}"を削除します。よろしいですか？'
  delete_success: '"{placeName}"を削除しました'
  messages:
    failed_to_load: 管理施設の読み込みに失敗しました
en:
  title: Rodents
  button:
    create: Create facility
  confirm_delete: 'This will delete "{placeName}". Are you sure?'
  delete_success: '"{placeName}" was deleted'
  messages:
    failed_to_load: Failed to load facilities
</i18n>

<script>
import Notifications from '@/mixins/notifications';
import { SENSOR_TYPES } from '@/mixins/sensorTypes';

import ConfirmDialog from '@/components/atoms/ConfirmDialog';
import PageTitleArea from '@/components/atoms/PageTitleArea';
import RoundButton from '@/components/atoms/RoundButton';

import RTypePlaceEditor from './RTypePlaceEditor';
import RTypePlaceEmptyState from './RTypePlaceEmptyState';
import RTypePlaceTable from './RTypePlaceTable';

// R 型設置場所に登録できるセンサー種別。ネズミを検知する 2 系統
// （自社 R 型センサーと RatSense）を束ねる単位として扱う。
const PLACEABLE_SENSOR_TYPES = [SENSOR_TYPES.RAT, SENSOR_TYPES.RAT_SENSE];

export default {
  name: 'r-type-places',
  mixins: [
    Notifications,
  ],
  components: {
    ConfirmDialog,
    PageTitleArea,
    RoundButton,
    RTypePlaceEditor,
    RTypePlaceEmptyState,
    RTypePlaceTable,
  },
  data() {
    return {
      places: [],
      sensors: [],
      loading: true,
      editorVisible: false,
      editTarget: null,
      deleteConfirmDialogVisible: false,
      deleteTarget: { name: '' },
    };
  },
  inject: ['api'],
  mounted: async function () {
    await this.loadPlaces();
  },
  methods: {
    loadPlaces: async function () {
      this.loading = true;
      try {
        const [places, sensors] = await Promise.all([
          this.api.fetchRTypePlaces(),
          this.api.fetchSensors(),
        ]);
        this.places = places;
        const filtered = sensors.filter(
          s => PLACEABLE_SENSOR_TYPES.includes(s.type) && s.permissions.includes('full'),
        );
        this.sensors = this._.uniqBy(filtered, 'id');
      } catch (error) {
        this.notifyError('messages.failed_to_load');
        this.places = [];
        this.sensors = [];
      }
      this.loading = false;
    },
    openEditor(place) {
      this.editTarget = place;
      this.editorVisible = true;
    },
    onSaved() {
      this.loadPlaces();
    },
    confirmDelete(place) {
      this.deleteTarget = place;
      this.deleteConfirmDialogVisible = true;
    },
    deletePlace: async function (place) {
      this.places = this.places.filter(p => p.id !== place.id);
      this.notifySuccess('delete_success', { placeName: place.name });
      await this.api.deleteRTypePlace(place.id);
    },
  },
  provide() {
    return {
      getRTypePlaces: () => this.places,
      getRTypeSensors: () => this.sensors,
    };
  },
  watch: {
    editorVisible(value) {
      if (!value) {
        this.editTarget = null;
      }
    },
  },
};
</script>
